import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  AlertTriangle,
  KeyRound,
  ShieldCheck,
  UserPen,
  X,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth.jsx";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PasswordInput } from "@/components/PasswordInput";
import { toast } from "sonner";

/**
 * Phase G — Security posture banner.
 *
 * Renders on top of every Platform page while the deployment is still on
 * shipped defaults. Three actions, all self-contained:
 *
 *   1) "Change password now"   →  POST /api/auth/change-password
 *   2) "Rename admin"          →  POST /api/auth/rename-admin (forces re-login)
 *   3) "Dismiss this advisory" →  POST /api/platform/security/dismiss-username-advisory
 *
 * The banner re-polls posture after every action; whichever one clears the
 * issue makes the banner disappear automatically. The dismissal is recorded
 * server-side (platform_settings) so it survives across browsers and users.
 */
export function SecurityPostureBanner() {
  const { t } = useTranslation();
  const { logout } = useAuth();
  const [posture, setPosture] = useState(null);
  const [pwOpen, setPwOpen] = useState(false);
  const [renameOpen, setRenameOpen] = useState(false);
  const [dismissOpen, setDismissOpen] = useState(false);

  // Password-change form
  const [oldp, setOldp] = useState("");
  const [newp, setNewp] = useState("");
  const [newp2, setNewp2] = useState("");

  // Rename form
  const [renameUser, setRenameUser] = useState("");
  const [renamePass, setRenamePass] = useState("");

  // Dismiss form
  const [dismissPass, setDismissPass] = useState("");

  const [busy, setBusy] = useState(false);

  const reload = async () => {
    try {
      const r = await api.get("/platform/security-posture");
      setPosture(r.data);
    } catch {
      // Endpoint missing on older backends — stay silent.
      setPosture(null);
    }
  };

  useEffect(() => {
    reload();
  }, []);

  if (!posture) return null;
  const showBanner =
    posture.default_admin_password_in_use ||
    posture.admin_username_is_default;
  if (!showBanner) return null;

  // --------------- Password change ---------------
  const submitPassword = async (e) => {
    e.preventDefault();
    if (newp !== newp2) {
      toast.error(t("posture.passwordsDontMatch", "Passwords don't match"));
      return;
    }
    if (newp.length < 12) {
      toast.error(
        t(
          "posture.passwordTooShort",
          "Pick a longer password (at least 12 characters)."
        )
      );
      return;
    }
    if (newp.toLowerCase().startsWith("elswitch")) {
      toast.error(
        t(
          "posture.passwordTooObvious",
          "Pick a password that doesn't start with 'elswitch'."
        )
      );
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/change-password", {
        old_password: oldp,
        new_password: newp,
      });
      toast.success(
        t("posture.passwordChanged", "Password updated. Banner is now hidden.")
      );
      setPwOpen(false);
      setOldp("");
      setNewp("");
      setNewp2("");
      await reload();
    } catch (err) {
      toast.error(
        err?.response?.data?.detail ||
          t("posture.changeFailed", "Could not change password.")
      );
    } finally {
      setBusy(false);
    }
  };

  // --------------- Rename admin ---------------
  const submitRename = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/auth/rename-admin", {
        current_password: renamePass,
        new_username: renameUser.trim().toLowerCase(),
      });
      if (r.data?.must_relogin) {
        toast.success(
          t(
            "posture.renamedRelogin",
            "Username updated. Logging you out — please sign in again with the new name."
          )
        );
        // Hand the user back to /login so they get a fresh session
        // bound to the new identifier.
        setTimeout(() => logout(), 1200);
      } else {
        toast.success(t("posture.renamed", "Username updated."));
        await reload();
      }
      setRenameOpen(false);
      setRenameUser("");
      setRenamePass("");
    } catch (err) {
      toast.error(
        err?.response?.data?.detail ||
          t("posture.renameFailed", "Could not rename admin.")
      );
    } finally {
      setBusy(false);
    }
  };

  // --------------- Dismiss advisory ---------------
  const submitDismiss = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/platform/security/dismiss-username-advisory", {
        current_password: dismissPass,
      });
      toast.success(
        t(
          "posture.dismissed",
          "Advisory dismissed. You can re-enable it by changing your username later."
        )
      );
      setDismissOpen(false);
      setDismissPass("");
      await reload();
    } catch (err) {
      toast.error(
        err?.response?.data?.detail ||
          t("posture.dismissFailed", "Could not dismiss advisory.")
      );
    } finally {
      setBusy(false);
    }
  };

  const showPwAction = posture.default_admin_password_in_use;
  const showUsernameActions = posture.admin_username_is_default;

  return (
    <>
      <Alert
        variant="destructive"
        className="mx-4 mt-4 border-[hsl(var(--danger))] bg-[hsl(var(--danger))/0.08]"
        data-testid="security-posture-banner"
      >
        <AlertTriangle className="h-4 w-4" />
        <AlertTitle className="font-semibold">
          {t(
            "posture.title",
            "Action required — your platform is using shipped default credentials."
          )}
        </AlertTitle>
        <AlertDescription className="text-sm space-y-2 mt-1">
          <ul className="list-disc list-inside space-y-0.5">
            {showPwAction && (
              <li data-testid="posture-pw">
                {t(
                  "posture.passwordWarn",
                  "The admin password is still 'elswitch'. Anyone reading these docs can sign in."
                )}
              </li>
            )}
            {showUsernameActions && (
              <li data-testid="posture-username">
                {t(
                  "posture.usernameAdvisory",
                  "Your admin username is 'admin' — half of every brute-force list. Rename it, or dismiss this advisory if you have a reason to keep the name."
                )}
              </li>
            )}
          </ul>
          <div className="flex gap-2 pt-1 flex-wrap">
            {showPwAction && (
              <Button
                size="sm"
                variant="default"
                className="gap-1"
                onClick={() => setPwOpen(true)}
                data-testid="posture-change-password"
              >
                <KeyRound className="h-3.5 w-3.5" />
                {t("posture.changePasswordBtn", "Change password now")}
              </Button>
            )}
            {showUsernameActions && (
              <>
                <Button
                  size="sm"
                  variant="default"
                  className="gap-1"
                  onClick={() => setRenameOpen(true)}
                  data-testid="posture-rename-admin"
                >
                  <UserPen className="h-3.5 w-3.5" />
                  {t("posture.renameAdminBtn", "Rename admin")}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="gap-1 text-muted-foreground hover:text-foreground"
                  onClick={() => setDismissOpen(true)}
                  data-testid="posture-dismiss"
                >
                  <X className="h-3.5 w-3.5" />
                  {t("posture.dismissBtn", "Dismiss this advisory")}
                </Button>
              </>
            )}
          </div>
        </AlertDescription>
      </Alert>

      {/* ---------- Change password dialog ---------- */}
      <Dialog open={pwOpen} onOpenChange={setPwOpen}>
        <DialogContent data-testid="posture-pw-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-[hsl(var(--accent))]" />
              {t("posture.dialogTitle", "Set a new admin password")}
            </DialogTitle>
            <DialogDescription>
              {t(
                "posture.dialogDesc",
                "Pick a long, unique password. After saving, this banner disappears automatically."
              )}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitPassword} className="space-y-3">
            <div>
              <Label className="text-xs">
                {t("posture.currentPassword", "Current password")}
              </Label>
              <PasswordInput
                value={oldp}
                onChange={(e) => setOldp(e.target.value)}
                autoComplete="current-password"
                required
                data-testid="posture-old"
              />
            </div>
            <div>
              <Label className="text-xs">
                {t("posture.newPassword", "New password")}{" "}
                <span className="text-muted-foreground font-normal">
                  {t("posture.minHint", "(≥12 chars)")}
                </span>
              </Label>
              <PasswordInput
                value={newp}
                onChange={(e) => setNewp(e.target.value)}
                autoComplete="new-password"
                minLength={12}
                required
                data-testid="posture-new"
              />
            </div>
            <div>
              <Label className="text-xs">
                {t("posture.confirmNewPassword", "Confirm new password")}
              </Label>
              <PasswordInput
                value={newp2}
                onChange={(e) => setNewp2(e.target.value)}
                autoComplete="new-password"
                required
                data-testid="posture-confirm"
              />
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setPwOpen(false)}
              >
                <X className="h-3.5 w-3.5 mr-1" />
                {t("common.cancel", "Cancel")}
              </Button>
              <Button type="submit" disabled={busy} data-testid="posture-submit">
                {busy
                  ? t("common.loading", "Saving…")
                  : t("posture.save", "Save & secure")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* ---------- Rename admin dialog ---------- */}
      <Dialog open={renameOpen} onOpenChange={setRenameOpen}>
        <DialogContent data-testid="posture-rename-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPen className="h-4 w-4 text-[hsl(var(--accent))]" />
              {t("posture.renameTitle", "Rename platform admin")}
            </DialogTitle>
            <DialogDescription>
              {t(
                "posture.renameDesc",
                "After saving, you'll be logged out automatically. Sign in with the new username next time."
              )}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitRename} className="space-y-3">
            <div>
              <Label className="text-xs">
                {t("posture.newUsername", "New username")}{" "}
                <span className="text-muted-foreground font-normal">
                  {t(
                    "posture.usernameHint",
                    "(3–32 chars, letters/digits/.-_)"
                  )}
                </span>
              </Label>
              <Input
                value={renameUser}
                onChange={(e) =>
                  setRenameUser(e.target.value.toLowerCase().replace(/\s+/g, ""))
                }
                placeholder="alice.doe"
                pattern="[a-z0-9][a-z0-9._-]{2,31}"
                required
                data-testid="posture-rename-user"
              />
            </div>
            <div>
              <Label className="text-xs">
                {t("posture.currentPassword", "Current password")}
              </Label>
              <PasswordInput
                value={renamePass}
                onChange={(e) => setRenamePass(e.target.value)}
                autoComplete="current-password"
                required
                data-testid="posture-rename-pass"
              />
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setRenameOpen(false)}
              >
                <X className="h-3.5 w-3.5 mr-1" />
                {t("common.cancel", "Cancel")}
              </Button>
              <Button type="submit" disabled={busy} data-testid="posture-rename-submit">
                {busy
                  ? t("common.loading", "Saving…")
                  : t("posture.renameSave", "Rename & log out")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* ---------- Dismiss advisory dialog ---------- */}
      <Dialog open={dismissOpen} onOpenChange={setDismissOpen}>
        <DialogContent data-testid="posture-dismiss-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <X className="h-4 w-4 text-muted-foreground" />
              {t("posture.dismissTitle", "Dismiss username advisory")}
            </DialogTitle>
            <DialogDescription>
              {t(
                "posture.dismissDesc",
                "Confirms that you've reviewed this and chosen to keep 'admin'. The banner will disappear. You can dismiss with your current password."
              )}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitDismiss} className="space-y-3">
            <div>
              <Label className="text-xs">
                {t("posture.currentPassword", "Current password")}
              </Label>
              <PasswordInput
                value={dismissPass}
                onChange={(e) => setDismissPass(e.target.value)}
                autoComplete="current-password"
                required
                data-testid="posture-dismiss-pass"
              />
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setDismissOpen(false)}
              >
                <X className="h-3.5 w-3.5 mr-1" />
                {t("common.cancel", "Cancel")}
              </Button>
              <Button type="submit" disabled={busy} data-testid="posture-dismiss-submit">
                {busy
                  ? t("common.loading", "Saving…")
                  : t("posture.dismissSave", "Dismiss")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default SecurityPostureBanner;
